<template>
  <v-dialog v-model="dialog" max-width="600px" scrollable>
    <v-card>
      <v-card-title>
        <span class="text-18 font-weight-bold">{{
          isEditMode ? "Editar Artista" : "Adicionar Novo Artista"
        }}</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <div
            @click="openFileInput('coverPhoto')"
            class="banner-placeholder rounded d-flex flex-column justify-center align-center"
            :style="{
              backgroundColor: '#E0E0E0',
              width: '100%',
              height: '150px',
              cursor: 'pointer',
              position: 'relative',
              backgroundImage: artistData.coverPhoto
                ? `url(${previewImages.coverPhoto})`
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              marginBottom: '55px',
            }"
          >
            <div
              v-if="!artistData.coverPhoto"
              class="d-flex flex-column align-center text-center mb-4"
            >
              <p class="mb-0 text-13 font-weight-medium">
                Clique para selecionar a Cover Atração
              </p>
              <small class="text-caption text--secondary mb-0">
                Formato recomendado: proporção 2:1, transparente
              </small>
            </div>
            <div
              @click.stop="openFileInput('profilePhoto')"
              class="profile-photo-placeholder"
              :style="{
                backgroundImage: artistData.profilePhoto
                  ? `url(${previewImages.profilePhoto})`
                  : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderColor: artistData.color,
              }"
            >
              <div v-if="!artistData.profilePhoto" class="text-center">
                <p class="mb-0 text-13 font-weight-medium">Foto de Perfil</p>
                <small class="text-caption text--secondary mb-0">1:1</small>
              </div>
            </div>
          </div>

          <!-- Form Fields -->
          <v-text-field
            v-model="artistData.name"
            label="Nome"
            required
            outlined
            :rules="[(v) => !!v || 'Nome é obrigatório']"
          ></v-text-field>

          <v-textarea
            v-model="artistData.description"
            label="Descrição"
            rows="3"
            outlined
            :rules="[(v) => !!v || 'Descrição é obrigatório']"
          ></v-textarea>
          <v-text-field
            v-model="artistData.shortDescription"
            label="Descrição curta"
            outlined
            :rules="[(v) => !!v || 'Descrição curta é obrigatório']"
          ></v-text-field>

          <v-text-field
            v-model="artistData.slug"
            prefix="artist/"
            outlined
            :rules="[
              (v) => !!v || 'Slug é obrigatório',
              (v) =>
                v.length <= 40 || 'O slug deve ter no máximo 40 caracteres',
            ]"
            @input="artistData.slug = slugify(artistData.slug)"
          ></v-text-field>
          <div class="d-flex align-center mb-4">
            <v-combobox
              v-model="artistData.category"
              :items="categories"
              label="Estilo Musical"
              required
              hide-details
              outlined
              :rules="[(v) => !!v || 'Estilo Musical é obrigatório']"
              class="mr-4"
            ></v-combobox>
            <v-menu :close-on-content-click="false" top>
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex">
                  <v-avatar
                    v-bind="attrs"
                    v-on="on"
                    :color="artistData.color"
                    class="white--text"
                  >
                    <v-icon x-large>mdi-palette</v-icon>
                  </v-avatar>
                </div>
              </template>

              <v-card class="pa-2 rounded-lg" style="max-width: 250px">
                <v-color-picker
                  v-model="artistData.color"
                  value-format="hex"
                  hide-mode-switch
                  swatches
                  :swatches="colors"
                />
              </v-card>
            </v-menu>
          </div>

          <!-- Spotify Section -->
          <v-card outlined class="pa-4">
            <p class="text-14 font-weight-bold">Trilha sonora</p>
            <v-text-field
              v-model="artistData.spotifyId"
              label="Spotify Link"
              hint="Link para uma playlist, música ou artista"
              dense
              outlined
              :rules="[() => validSpotifyLink || 'Link inválido']"
            />
            <v-card
              v-if="!artistData.spotifyId || !validSpotifyLink"
              color="d-flex flex-column align-center pa-4"
              elevation="0"
            >
              <v-icon left x-large>mdi-spotify</v-icon>
              <p class="mb-0">Adicione um link do Spotify para ver a prévia</p>
            </v-card>
            <spotify-embed v-else :id="artistData.spotifyId" class="w-full" />
          </v-card>

          <!-- Vertical Cover Photo -->
          <div class="image-upload-container">
            <v-card outlined class="pa-4">
              <p class="text-14 font-weight-bold mb-2 black--text">Artes</p>

              <div class="d-flex">
                <div>
                  <v-img
                    v-if="artistData.verticalCoverPhoto"
                    class="vertical-cover-preview mr-2"
                    :aspect-ratio="8 / 10"
                    :src="previewImages.verticalCoverPhoto"
                    :width="200 * (8 / 10)"
                    :height="200"
                  ></v-img>
                </div>
                <div
                  @click="openFileInput('verticalCoverPhoto')"
                  class="banner-placeholder rounded d-flex flex-column justify-center align-center"
                  :style="{
                    backgroundColor: '#E0E0E0',
                    width: artistData.verticalCoverPhoto
                      ? 'calc(100% - 170px)'
                      : '100%',
                    height: '200px',
                    cursor: 'pointer',
                  }"
                >
                  <div class="d-flex flex-column align-center text-center">
                    <p class="mb-0 text-13 font-weight-medium">
                      Clique para selecionar o Cover em Pé
                    </p>
                    <small class="text-caption text--secondary mb-0">
                      Formato recomendado: proporção 9:16 ou mais esticado
                    </small>
                  </div>
                </div>
              </div>
            </v-card>
          </div>

          <!-- Hidden File Inputs -->
          <v-file-input
            v-show="false"
            v-model="fileInputs.profilePhoto"
            accept="image/*"
            @change="onImageSelected('profilePhoto')"
            ref="profilePhotoInput"
          ></v-file-input>

          <v-file-input
            v-show="false"
            v-model="fileInputs.coverPhoto"
            accept="image/*"
            @change="onImageSelected('coverPhoto')"
            ref="coverPhotoInput"
          ></v-file-input>

          <v-file-input
            v-show="false"
            v-model="fileInputs.verticalCoverPhoto"
            accept="image/*"
            @change="onImageSelected('verticalCoverPhoto')"
            ref="verticalCoverPhotoInput"
          ></v-file-input>
        </v-form>
      </v-card-text>
      <v-alert v-if="error" type="error" class="mx-3">
        {{ error }}
      </v-alert>
      <v-card-actions>
        <v-btn
          color="primary"
          text
          :disabled="loading"
          @click="openGenerateArtist"
        >
          <v-icon left>mdi-auto-fix</v-icon>
          Gerar com IA
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close">Cancelar</v-btn>
        <v-btn
          color="primary "
          @click="save"
          :disabled="!valid"
          :loading="loading"
          >Salvar</v-btn
        >
      </v-card-actions>
    </v-card>
    <GenerateArtistDialog
      ref="generateArtistDialog"
      @confirm="onArtistSuggestion"
    />
  </v-dialog>
</template>

<script>
import SpotifyEmbed from "@/components/global/SpotifyEmbed.vue";
import GenerateArtistDialog from "./GenerateArtistDialog.vue";
import registrationsService from "@/services/admin/registrations";
import ImageConverter from "@/utils/ImageConverter";
import slugifyUtil from "@/utils/slugfy";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

function base64ToFile(base64, mimeType, filename) {
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new File([new Blob([arr], { type: mimeType })], filename, {
    type: mimeType,
  });
}

export default {
  name: "NewArtistDialog",
  props: {
    value: Boolean,
    artist: {
      type: Object,
      default: null,
    },
  },
  components: { SpotifyEmbed, VuePerfectScrollbar, GenerateArtistDialog },
  data() {
    return {
      valid: true,
      dialog: this.value,
      validSpotifyRegex: /((album|playlist|track|artist)\/)\S*/g,
      isEditMode: false,
      loading: false,
      error: null,
      artistData: {},
      fileInputs: {
        profilePhoto: null,
        coverPhoto: null,
        verticalCoverPhoto: null,
      },
      categories: [
        "Rock",
        "Pop",
        "Sertanejo",
        "Funk",
        "MPB",
        "Eletrônica",
        "Hip Hop",
        "Jazz",
        "Blues",
        "Outros",
      ],
      previewImages: {
        profilePhoto: null,
        coverPhoto: null,
        verticalCoverPhoto: null,
      },
      colors: [
        "#E91E63", // pink
        "#9C27B0", // purple
        "#3F51B5", // indigo
        "#009688", // teal
        "#4CAF50", // green (success)
        "#CDDC39", // lime
        "#FFC107", // amber
        "#F44336", // red (error)
        "#795548", // brown
        "#FFFFFF", // white
        "#000000", // black
      ],
    };
  },
  watch: {
    value(val) {
      this.dialog = val;
    },
    "artistData.slug": function (newValue) {
      this.artistData.slug = this.slugify(newValue);
    },
    dialog(val) {
      this.$emit("input", val);
      if (val) {
        this.initializeData();
      }
    },
    "artistData.spotifyId": {
      handler(value) {
        if (!value) return;
        [value] = value.split("?");
        const regex = /((?:album|playlist|track|artist)\/)\S*/g;
        const match = value.match(regex);
        if (match) {
          const [id] = match;
          this.artistData.spotifyId = id;
        }
      },
      immediate: true,
    },
  },
  computed: {
    validSpotifyLink() {
      const id = this.artistData.spotifyId || "";
      return !!id.match(this.validSpotifyRegex);
    },
  },
  methods: {
    slugify(text) {
      return slugifyUtil(text || "").slice(0, 40);
    },
    openGenerateArtist() {
      this.$refs.generateArtistDialog.open({
        name: this.artistData.name || "",
      });
    },
    onArtistSuggestion(suggestion) {
      this.error = null;
      this.artistData.name = suggestion.name || this.artistData.name;
      this.artistData.description =
        suggestion.description || this.artistData.description;
      this.artistData.shortDescription =
        suggestion.shortDescription || this.artistData.shortDescription;
      this.artistData.category =
        suggestion.category || this.artistData.category;
      this.artistData.color = suggestion.color || this.artistData.color;
      this.artistData.spotifyId =
        suggestion.spotifyId || this.artistData.spotifyId;
      this.artistData.slug = this.slugify(
        suggestion.slug || suggestion.name || this.artistData.name
      );

      this.applySuggestedImage("profilePhoto", suggestion.profilePhoto);
      this.applySuggestedImage("coverPhoto", suggestion.coverPhoto);
      this.applySuggestedImage(
        "verticalCoverPhoto",
        suggestion.verticalCoverPhoto
      );

      this.$nextTick(() => {
        this.$refs.form?.validate?.();
      });
    },
    applySuggestedImage(field, imageItem) {
      if (!imageItem?.image) return;
      const mimeType = imageItem.mimeType || "image/jpeg";
      const ext = mimeType.split("/")[1] || "jpg";
      const file = base64ToFile(
        imageItem.image,
        mimeType,
        `${field}-${Date.now()}.${ext}`
      );
      this.artistData[field] = file;
      this.fileInputs[field] = file;
      this.previewImages[field] = `data:${mimeType};base64,${imageItem.image}`;
    },
    initializeData() {
      // Reset fileInputs
      this.fileInputs = {
        profilePhoto: null,
        coverPhoto: null,
        verticalCoverPhoto: null,
      };
      if (this.artist) {
        // Edit mode
        this.isEditMode = true;
        this.artistData = JSON.parse(JSON.stringify(this.artist));
        this.setPreviewImages();
      } else {
        // Create mode
        this.isEditMode = false;
        this.artistData = {
          name: "",
          description: "",
          shortDescription: "",
          category: "",
          slug: "",
          profilePhoto: null,
          coverPhoto: null,
          verticalCoverPhoto: null,
          spotifyId: "",
          color: "white",
        };
        this.previewImages = {
          profilePhoto: null,
          coverPhoto: null,
          verticalCoverPhoto: null,
        };
      }
    },
    setPreviewImages() {
      ["profilePhoto", "coverPhoto", "verticalCoverPhoto"].forEach((key) => {
        if (this.artistData[key]) {
          if (typeof this.artistData[key] === "string") {
            // URL
            this.previewImages[key] = this.artistData[key];
          } else if (this.artistData[key] instanceof File) {
            // File object
            const reader = new FileReader();
            reader.onload = (e) => {
              this.previewImages[key] = e.target.result;
            };
            reader.readAsDataURL(this.artistData[key]);
          }
        } else {
          this.previewImages[key] = null;
        }
      });
    },
    close() {
      this.dialog = false;
      this.$refs.form.reset();
      // Reset fileInputs
      this.fileInputs = {
        profilePhoto: null,
        coverPhoto: null,
        verticalCoverPhoto: null,
      };
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const artistData = {
            name: this.artistData.name,
            category: this.artistData.category,
            description: this.artistData.description,
            shortDescription: this.artistData.shortDescription,
            slug: this.artistData.slug,
            spotifyId: this.artistData.spotifyId,
            color: this.artistData.color,
          };

          if (!this.isEditMode) {
            artistData.slug = this.artistData.slug;
          }

          let response;
          if (this.isEditMode) {
            response = await registrationsService.updateArtist(
              this.artistData.id,
              artistData
            );
          } else {
            response = await registrationsService.createArtist(artistData);
          }

          const artistId = response.artistId || this.artistData.id;
          await this.uploadArtistImages(artistId);
          const updatedArtist = {
            ...this.artistData,
            id: artistId,
            profilePhoto: this.previewImages.profilePhoto,
            coverPhoto: this.previewImages.coverPhoto,
            verticalCoverPhoto: this.previewImages.verticalCoverPhoto,
          };

          this.close();
          this.$emit("artist-saved", updatedArtist);
        } catch (error) {
          console.error(error);
          this.error = error.message || "Erro ao salvar artista";
        } finally {
          this.loading = false;
        }
      }
    },
    async uploadArtistImages(artistId) {
      const imageConverter = new ImageConverter();

      const images = [
        { file: this.artistData.profilePhoto, type: "profilePhoto" },
        { file: this.artistData.coverPhoto, type: "coverPhoto" },
        {
          file: this.artistData.verticalCoverPhoto,
          type: "verticalCoverPhoto",
        },
      ];

      for (const image of images) {
        if (image.file && image.file instanceof File) {
          try {
            const settings = this.getImageSettings(image.type);
            const webpFile = await this.convertImage(
              image.file,
              imageConverter,
              settings
            );

            const formData = new FormData();
            formData.append("file", webpFile);

            await registrationsService.uploadArtistImage(
              artistId,
              image.type,
              formData
            );
          } catch (error) {
            console.error(`Erro ao processar e enviar ${image.type}:`, error);
            this.error = `Erro ao processar e enviar ${image.type}: ${error.message}`;
          }
        }
      }
    },
    getImageSettings(imageType) {
      switch (imageType) {
        case "profilePhoto":
          return {
            maxWidth: 500,
            maxHeight: 500,
            aspectRatio: 1,
            allowTransparency: false,
            backgroundColor: "white",
          };
        case "coverPhoto":
          return {
            maxWidth: 1920,
            maxHeight: 960,
            aspectRatio: 2,
            allowTransparency: false,
            backgroundColor: "white",
          };
        case "verticalCoverPhoto":
          return {
            maxWidth: 652,
            maxHeight: 815,
            aspectRatio: 8 / 10,
            allowTransparency: false,
            backgroundColor: "white",
          };
        default:
          return {};
      }
    },
    async convertImage(file, converter, settings) {
      if (!file) return null;
      try {
        return await converter.webp(file, settings);
      } catch (error) {
        return file;
      }
    },
    openFileInput(inputType) {
      const inputMap = {
        profilePhoto: "profilePhotoInput",
        coverPhoto: "coverPhotoInput",
        verticalCoverPhoto: "verticalCoverPhotoInput",
      };
      this.$refs[inputMap[inputType]].$refs.input.click();
    },
    onImageSelected(inputType) {
      const file = this.fileInputs[inputType];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewImages[inputType] = e.target.result;
        };
        reader.readAsDataURL(file);
        this.artistData[inputType] = file;
      } else {
        this.previewImages[inputType] = null;
        if (!this.isEditMode) {
          this.artistData[inputType] = null;
        }
      }
    },
    selectColor(color) {
      this.artistData.color = color;
    },
  },
};
</script>

<style scoped>
.file-input-box {
  background-color: #f5f5f5;
  border-radius: 4px;
}

.file-input-box ::v-deep .v-file-input__text {
  text-align: center;
  width: 100%;
}

.file-input-box ::v-deep .v-input__slot {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-upload-container {
  margin-top: 20px;
}

.profile-photo-placeholder {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(50%);
  background-color: #f0f0f0;
  border-radius: 50%;
  width: 100px;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: 4px solid;
}
</style>
